import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getMfaPrecheck, getUser } from "src/redux/selectors";
import { setMfaPrecheck } from "src/redux/slices";

/**
 * Sign-in flow:
 *  1. User signs in (phone or email/password)
 *  2. Firebase onAuthStateChanged fires, useLoadSnackpassUser runs, sets
 *     Redux User state. This in turn triggers requireEmailVerification to
 *     be true.
 *  3. The user's auth token cookie is checked. If valid, skip to 5.
 *  4. If not, the user is emailed a passcode and submits it, then an auth
 *     token is issued and set as a browser cookie.
 *  5. `hasVerifiedEmail` is set to true
 */
export function useMFA() {
    // mfaPrecheck is true only when the server cookie check has succeeded
    // (signinPreload sets it on page load). The gate below must require
    // verification when it is FALSE; inverting this term disables the second
    // factor for every fresh sign-in.
    const dispatch = useAppDispatch();
    const mfaPrecheck = useAppSelector(getMfaPrecheck);
    const user = useSelector(getUser);
    const userId = user?._id;

    // Verification state is keyed by user id so it can never carry across an
    // identity change: the predicate fails closed during the very render in
    // which a different user appears, not in an effect one frame later (by
    // which point the authenticated router would already have mounted).
    const [verifiedUserId, setVerifiedUserId] = useState<string | undefined>(
        undefined,
    );
    const [precheckUserId, setPrecheckUserId] = useState<string | undefined>(
        undefined,
    );

    // Bind the preload precheck to the first signed-in user it is observed
    // with. State adjustment during render: React re-renders before
    // committing, so no frame is painted with the unbound value.
    if (mfaPrecheck && userId && precheckUserId === undefined) {
        setPrecheckUserId(userId);
    }

    // Capture the identity from the render that started verification. An
    // in-flight response for user A must not verify user B if the signed-in
    // account changes before that response resolves.
    const setHasVerifiedEmail = useCallback(
        (hasVerifiedEmail: boolean) => {
            setVerifiedUserId(hasVerifiedEmail ? userId : undefined);
        },
        [userId],
    );

    const previousUserIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const previousUserId = previousUserIdRef.current;
        previousUserIdRef.current = userId;
        // Any identity change — sign-out (userId undefined) or a direct
        // user replacement — drops the previous user's verification state.
        // Initial bootstrap (no previous user) must not reset, or a precheck
        // that signinPreload set before mount would be erased. The redux flag
        // can only be cleared here (no dispatch during render), but the
        // id-keyed predicate already ignores it for a different user, so no
        // fail-open frame exists in the replacement case either.
        if (previousUserId && previousUserId !== userId) {
            setVerifiedUserId(undefined);
            dispatch(setMfaPrecheck(false));
        }
    }, [userId, dispatch]);

    const hasVerifiedEmail = !!userId && verifiedUserId === userId;
    const precheckPassed = mfaPrecheck && !!userId && precheckUserId === userId;

    const requireEmailVerification =
        !precheckPassed && !!user && !hasVerifiedEmail;

    return {
        setHasVerifiedEmail,
        requireEmailVerification,
    };
}
